import { FC, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { ButtonPrimary } from 'shared/components/Button';
import { ContainerMedium } from 'shared/components/Container';
import { Input } from 'shared/components/Input';
import { Spacer } from 'shared/components/Layout';
import { Wrapper } from 'shared/components/Wrapper';
import { onlyDigitValidation } from 'shared/validations/validations';
import { mischka, athensGray } from 'shared/config/Colors';

export const DealerPercentageContainer = styled(Wrapper)`
  background-color: ${athensGray};
  border: 2px solid ${mischka};
  border-radius: 16px;
  padding: 10px 0 10px 16px;
  margin-left: 16px;
`;

const PercentInputWrapper = styled.div`
  max-width: 112px;
  position: relative;

  &::after {
    align-items: center;
    bottom: 0;
    color: ${({ theme }) => theme.silverSandTwo};
    content: '%';
    display: flex;
    font-size: 14px;
    left: calc(100% + 7px);
    margin: auto;
    position: absolute;
    top: 0;
  }
`;

const InputLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 4px;
  text-transform: uppercase;
  text-align: center;
`;

const InputColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export type DealerPercentageFieldName = 'freight' | 'grossMargin' | 'salesTax';

export const isNullOrEmpty = (
  value: string | number | null | undefined
): boolean => {
  return value == null || value === '';
};

// Keep only digits and the first dot (US decimal format)
const sanitizePercentage = (value: string): string => {
  const [whole, ...decimals] = value.replace(/[^\d.]/g, '').split('.');

  return decimals.length ? `${whole}.${decimals.join('')}` : whole;
};

// If the Dealer Custom field has a value (including 0), use that
// If the field is empty, use the Dealer Default value
export const getAppliedPercentage = (
  customValue: string | null | undefined,
  defaultValue: number
): number => {
  return isNullOrEmpty(customValue)
    ? defaultValue
    : parseFloat(customValue as string);
};

interface DealerPercentageInputProps {
  name: DealerPercentageFieldName;
  canEdit: boolean;
  defaultValue: number; // Dealer Default Percentage
  customValue: number | null; // Dealer Custom Percentage saved on order
  max: number;
  onSave?: (customValue: number | null) => void;
}

export const DealerPercentageInput: FC<DealerPercentageInputProps> = ({
  name,
  canEdit,
  defaultValue,
  customValue,
  max,
  onSave,
}) => {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();

  const value = watch(name);
  const [initialValue, setInitialValue] = useState<number | null>(null);

  const { onChange, ...field } = register(
    name,
    onlyDigitValidation({ min: 0, max })
  );

  // Initialize the field with customValue when component mounts or customValue changes
  useEffect(() => {
    setValue(name, customValue != null ? customValue.toString() : '');
    setInitialValue(customValue ?? null);
  }, [name, customValue, setValue]);

  // Check if value has been changed from initial value
  const hasValueChanged = () => {
    const currentValue = isNullOrEmpty(value) ? null : parseFloat(value);

    return currentValue !== initialValue;
  };

  return (
    <ContainerMedium
      flex
      middle
      center
      isTableCell
      border={['right']}
      noVertialSpacing
    >
      <InputColumn>
        <InputLabel>Dealer Default</InputLabel>
        <PercentInputWrapper>
          <Input
            value={defaultValue}
            readOnly
            disabled
            className="review-pricing-percentage-input"
          />
        </PercentInputWrapper>
      </InputColumn>

      <Spacer w="40px" />

      <InputColumn>
        <InputLabel>Dealer Custom</InputLabel>
        <PercentInputWrapper>
          <Input
            {...field}
            onChange={(e) => {
              e.target.value = sanitizePercentage(e.target.value);
              return onChange(e);
            }}
            inputMode="decimal"
            readOnly={!canEdit}
            aria-invalid={errors[name] ? 'true' : 'false'}
            className="review-pricing-percentage-input"
            placeholder=""
          />
        </PercentInputWrapper>
      </InputColumn>

      {hasValueChanged() && (
        <>
          <Spacer w="40px" />
          <ButtonPrimary
            disabled={!canEdit || !!errors[name]}
            onClick={() => {
              // If field is empty, send null to clear custom value
              // Otherwise send the parsed number value
              const customAmount = isNullOrEmpty(value)
                ? null
                : parseFloat(value);

              onSave?.(customAmount);
              // Update initial value to current value after save
              setInitialValue(customAmount);
            }}
          >
            Save
          </ButtonPrimary>
        </>
      )}
    </ContainerMedium>
  );
};
